import { CircleAlert, CircleCheck, Pause, Trash2, TriangleAlert, Wallet, type LucideIcon } from 'lucide-react'
import { useId, type ReactNode, type RefObject } from 'react'
import { Async, type AsyncState } from '../../platform/async/Async'
import { Skeleton } from '../../platform/async/Skeleton'
import { CategoryIcon } from '../../platform/icons/CategoryIcon'
import { Icon } from '../../platform/icons/Icon'
import type { LocalDate } from '../../platform/localDate'
import { BackLink } from '../../platform/primitives/BackLink'
import { Button } from '../../platform/primitives/Button'
import { ButtonLink } from '../../platform/primitives/ButtonLink'
import { Money } from '../../platform/primitives/Money'
import { Panel } from '../../platform/primitives/Panel'
import { useBreakpoint } from '../../platform/responsive/useBreakpoint'
import type { Category } from '../categories/categoryTree'
import { daysLeft, envelopeStatus, periodBounds, periodLabel, periodRange, shownPct, type EnvelopeStatus } from './budgetRules'
import { BUDGET_PATH } from './budgetPaths'
import { BUDGET_CURRENCY, type Envelope } from './budgetWire'
import { envelopeTransactionsHref } from './envelopeTransactions'
import { EnvelopeBar } from './EnvelopeBar'
import './budget.css'

/** Every word on the page that is not a figure or the user's own text (the tests import this). */
export const DETAIL_COPY = {
  back: 'All envelopes',
  deleteBody: 'This removes the envelope only. Your transactions are not changed.',
  over: 'Over budget',
  status: {
    over: 'Over budget',
    close: 'Close to limit',
    on_track: 'On track',
    untouched: 'Nothing spent yet',
    paused: 'Paused',
  },
  gone: { title: "This envelope isn't here", body: 'It may have been deleted on another device, or the link is out of date.' },
} as const

const STATUS_ICON: Record<EnvelopeStatus, LucideIcon> = {
  over: TriangleAlert,
  close: CircleAlert,
  on_track: CircleCheck,
  untouched: CircleCheck,
  paused: Pause,
}

type Props = {
  state: AsyncState<Envelope>
  categories: readonly Category[] | null
  today: LocalDate
  /** The page's posted-expenses panel; the container owns its query. */
  transactions: (envelope: Envelope) => ReactNode
  onEdit: () => void
  onTogglePause: () => void
  onDelete: () => void
  /** The Edit button, so the sheet can hand focus back to it. */
  editRef: RefObject<HTMLButtonElement | null>
  /** A pause or resume is in flight. */
  busy: boolean
  /** "All envelopes" from the not-found state. */
  onBack: () => void
}

function Skeletons({ compact }: { compact: boolean }) {
  return (
    <div className="bd-body">
      <Panel className={`bd-hero${compact ? ' bd-hero--compact' : ''}`}>
        <div className="bd-hero__id">
          <Skeleton height={compact ? 34 : 52} width={compact ? 34 : 52} />
          <div className="bd-hero__text">
            <Skeleton height={22} width="30%" />
            <Skeleton height={13} width="40%" />
            <Skeleton height={12} width="35%" />
          </div>
        </div>
        <Skeleton height={34} width={140} />
      </Panel>
      <div className="bd-tiles">
        {[0, 1, 2, 3].map((i) => (
          <Panel key={i} className="bd-tile">
            <Skeleton height={10} width="50%" />
            <Skeleton height={24} width="45%" />
          </Panel>
        ))}
      </div>
      <Panel className="bd-list">
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} height={44} />
        ))}
      </Panel>
    </div>
  )
}

function Tile({ label, value, sub }: { label: string; value: ReactNode; sub?: string }) {
  const labelId = useId()
  const valueId = useId()
  return (
    <Panel role="group" aria-labelledby={`${labelId} ${valueId}`} className="bd-tile">
      <span id={labelId} className="bd-tile__label">
        {label}
      </span>
      <span id={valueId} className="bd-tile__value">
        {value}
      </span>
      {sub !== undefined && <span className="bd-tile__sub">{sub}</span>}
    </Panel>
  )
}

function Hero({
  e,
  category,
  compact,
  href,
  editRef,
  busy,
  onEdit,
  onTogglePause,
  onDelete,
}: {
  e: Envelope
  category: Category | undefined
  compact: boolean
  href: string | null
} & Pick<Props, 'editRef' | 'busy' | 'onEdit' | 'onTogglePause' | 'onDelete'>) {
  const status = envelopeStatus(e)
  const paused = status === 'paused'
  const StatusGlyph = STATUS_ICON[status]
  const period = periodLabel(e.period) ?? 'Monthly'
  // The status line carries paused / on track / over; the meta line is only what the envelope IS.
  const meta = [period, category?.name ?? 'All spending'].join(' · ')
  return (
    <Panel className={`bd-hero${compact ? ' bd-hero--compact' : ''}`}>
      <div className="bd-hero__id">
        <span className="bd-hero__icon">
          {category !== undefined ? (
            <CategoryIcon category={category} />
          ) : (
            <span data-icon="all-spending">
              <Icon glyph={Wallet} />
            </span>
          )}
        </span>
        <div className="bd-hero__text">
          <h2 className="bd-hero__name">{e.name}</h2>
          <p className={`bd-hero__status bd-hero__status--${status}`}>
            <span aria-hidden="true">
              <Icon glyph={StatusGlyph} size={14} />
            </span>
            <span data-status>{DETAIL_COPY.status[status]}</span>
          </p>
          <p className="bd-hero__meta">{meta}</p>
        </div>
      </div>
      <div className="bd-hero__side">
        <Money cents={e.progress.spentCents} currency={BUDGET_CURRENCY} variant="hero" kind="stock" />
        <p className="bd-hero__of">
          {'of '}
          <Money cents={e.amountCents} currency={BUDGET_CURRENCY} kind="stock" />
          {` · ${shownPct(e)}% used`}
        </p>
        {!paused && (
          <>
            <EnvelopeBar envelope={e} />
            <p className="bd-hero__remain" data-remaining>
              {status === 'over' ? (
                DETAIL_COPY.over
              ) : (
                <>
                  <Money cents={e.progress.remainingCents} currency={BUDGET_CURRENCY} kind="stock" />
                  {' left'}
                </>
              )}
            </p>
          </>
        )}
      </div>
      <div className="bd-hero__actions">
        <Button ref={editRef} variant={paused ? 'outline' : 'primary'} size="panel" onClick={onEdit}>
          Edit
        </Button>
        {/* Stays enabled while the request is in flight (a re-click is ignored by the container), and says what it is doing. */}
        <Button variant={paused ? 'primary' : 'outline'} size="panel" aria-busy={busy || undefined} onClick={onTogglePause}>
          {busy ? (paused ? 'Resuming…' : 'Pausing…') : paused ? 'Resume' : 'Pause'}
        </Button>
        <Button variant="destructive" size="panel" onClick={onDelete}>
          Delete
        </Button>
        {href !== null && (
          <ButtonLink to={href} variant="outline" size="panel">
            View in Transactions
          </ButtonLink>
        )}
      </div>
    </Panel>
  )
}

/**
 * `/budget/:id`, presentational. Every figure is the server's (spent, allowance, percentage, remaining);
 * nothing here adds, subtracts or divides money. Over is said in words, never as "$x over" (B2). The
 * hero is an opaque tile with one primary action (rule 9): Edit, or Resume on a paused envelope.
 */
export function BudgetDetailScreen({ state, categories, today, transactions, onEdit, onTogglePause, onDelete, editRef, busy, onBack }: Props) {
  const compact = useBreakpoint() === 'phone'
  return (
    <div className={`bd-page bd-page--${compact ? 'phone' : 'wide'}`}>
      <BackLink to={BUDGET_PATH}>{DETAIL_COPY.back}</BackLink>
      <Async
        surface="budgets"
        state={state}
        skeleton={<Skeletons compact={compact} />}
        empty={{
          icon: <Icon glyph={Trash2} size={24} />,
          title: DETAIL_COPY.gone.title,
          body: DETAIL_COPY.gone.body,
          action: { label: DETAIL_COPY.back, onAct: onBack },
        }}
      >
        {(e) => {
          const category = e.categoryId === null ? undefined : categories?.find((c) => c.id === e.categoryId)
          const bounds = periodBounds(e.period, today)
          const [y = 0, m = 1, d = 1] = today.split('-').map(Number)
          const href = envelopeTransactionsHref(e, categories, new Date(y, m - 1, d, 12))
          const left = daysLeft(bounds, today)
          return (
            <div className="bd-body">
              <Hero
                e={e}
                category={category}
                compact={compact}
                href={href}
                editRef={editRef}
                busy={busy}
                onEdit={onEdit}
                onTogglePause={onTogglePause}
                onDelete={onDelete}
              />
              <div className="bd-tiles">
                <Tile label="Period" value={periodRange(bounds)} />
                <Tile label="Days left" value={left === 0 ? 'Last day' : String(left)} />
                <Tile label="Tracking" value={category?.name ?? 'All spending'} />
                <Tile label="Alert at" value={`${e.alertThresholdPct}%`} sub="of the allowance" />
              </div>
              {transactions(e)}
            </div>
          )
        }}
      </Async>
    </div>
  )
}
